import { useEffect } from "react"

import { Link, useRouteError } from "react-router-dom"

import { ErrorReportDialog } from "@/components/error-report-dialog"
import { AlertTriangle } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { useRoutes } from "@/contexts/use-routes"
import { useErrorBoundaryMessage } from "@/hooks/use-error-boundary-message"
import { reportClientError } from "@/lib/telemetry"

export function ErrorBoundary() {
  const { errorMessage, errorStatus, retry } = useErrorBoundaryMessage()
  const { routes } = useRoutes()
  const routeError = useRouteError()

  // Emit telemetry when a routed error surfaces here. Dedup in `reportClientError`
  // collapses this with any react-query / window.onerror emission of the same
  // error. 404 / route-match errors aren't runtime bugs — skip those.
  useEffect(() => {
    if (routeError === undefined || routeError === null) return
    if (errorStatus === 404) return
    reportClientError(routeError, { source: "router-error-boundary" })
  }, [routeError, errorStatus])

  // Find the default route or fallback to "/" which will redirect
  const defaultRoute = routes.find((r) => r.default_route)
  const homePath = defaultRoute?.path ?? "/"

  return (
    <section className="bg-background flex h-[calc(100vh-64px)] items-center justify-center p-4">
      <Card className="border-destructive/50 w-full max-w-md text-center">
        <CardHeader>
          <section className="mb-4 flex justify-center">
            <AlertTriangle className="text-destructive h-12 w-12" />
          </section>
          <CardTitle className="text-destructive text-2xl">Something Went Wrong</CardTitle>
          <CardDescription className="pt-2">
            Sorry, we encountered an unexpected issue. Please try again.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-muted-foreground space-y-2 text-sm">
          {errorStatus && (
            <p>
              <b>Status Code:</b> {errorStatus}
            </p>
          )}
          <p className="italic">{errorMessage || "No specific error message provided."}</p>
        </CardContent>
        <CardFooter className="flex flex-wrap justify-center gap-2">
          <Button variant="destructive" onClick={retry}>
            Retry
          </Button>
          <Button asChild variant="outline">
            <Link to={homePath}>Go Home</Link>
          </Button>
          <ErrorReportDialog />
        </CardFooter>
      </Card>
    </section>
  )
}
